import * as v from "valibot";

import { booleanField } from "src/helpers/valibot";
import { KEY } from "src/consts/nexus-keys";

export const endpointToggleSchema = v.optional(v.object({ payload: v.object({ toggle: v.picklist(["on", "off"]) }) }), {
  payload: { toggle: "off" }
});

// Nexus may send ids as number or string; transform normalizes every element so
// downstream strict comparisons (e.g. Array.includes in useGateway) stay correct.
export const nexusSchema = v.object({
  [KEY.IT_VALIDATION_FIELD]: v.object({
    payload: v.object({
      CHN: v.object({
        account_number: v.object({
          cc_number: v.array(v.number())
        })
      })
    })
  }),
  [KEY.FLAG_ENDPOINT_TRX_MICROSERVICES]: v.object({
    payload: v.object({
      // Flat tree: endpoint -> group -> { actions, enable }. Use a record so new groups
      // sent by Nexus aren't stripped on parse.
      endpoint: v.record(
        v.string(),
        v.object({
          // Companies allowed onto the new microservice for this group ("all" => everyone).
          // Normalize ids to string for useGateway's `${company.id}` comparison.
          enable: v.optional(v.array(v.pipe(v.union([v.string(), v.number()]), v.transform(String))), []),
          // Action names this group routes to the microservice.
          actions: v.optional(v.array(v.string()))
        })
      )
    })
  }),
  [KEY.COMPANY_CUSTOM_FILTER_START_DATE]: v.object({
    payload: v.object({
      transaction_history: v.array(v.string())
    })
  }),
  [KEY.FLAG_ENDPOINT_CMS]: v.object({
    payload: v.object({
      company_id: v.array(v.string())
    })
  }),
  [KEY.FLAG_ENDPOINT_ONBOARDING]: v.object({
    // Flat map of migration flag -> boolean (e.g. isNewAuthentication: true).
    payload: v.record(v.string(), v.boolean())
  }),
  [KEY.FLAG_ENDPOINT_ITR]: v.object({
    payload: v.object({
      // Flat tree: endpoint -> group -> { actions, enable }. Use a record so new groups
      // sent by Nexus aren't stripped on parse.
      endpoint: v.record(
        v.string(),
        v.object({
          // Companies allowed onto the new microservice for this group ("all" => everyone).
          // Normalize ids to string for useGateway's `${company.id}` comparison.
          enable: v.optional(v.array(v.pipe(v.union([v.string(), v.number()]), v.transform(String))), []),
          // Action names this group routes to the microservice.
          actions: v.optional(v.array(v.string()))
        })
      )
    })
  }),
  // Mid-session refetch tuning. Optional-with-default so a Nexus response that omits
  // this flag still parses (and doesn't drag every other flag back to defaults).
  // `ttl_seconds` is the dedupe FLOOR between refetches — clamped in refetch-controller.ts.
  [KEY.NEXUS_REFETCH_CONFIG]: v.optional(v.object({ payload: v.object({ ttl_seconds: v.optional(v.number(), 60) }) }), {
    payload: { ttl_seconds: 60 }
  }),
  // Per-group onboarding flags. Each shares `endpointToggleSchema` ({ toggle: "on" | "off" }).
  [KEY.GENERAL]: endpointToggleSchema,
  [KEY.API_INFO]: endpointToggleSchema,
  [KEY.MODULE_ACCESS_OTP]: endpointToggleSchema,
  [KEY.NEW_ITR_FLOW]: endpointToggleSchema,
  [KEY.ITR_MAKER_APPROVER_FLOW]: endpointToggleSchema,
  // Per-API Kong kill-switch. `true` → route through Kong; `false` → keep the
  // current live path. Keyed by API (one per call site in API_ENDPOINTS_REPORT_PAY.md).
  [KEY.FLAG_ENDPOINT_PAY]: v.object({
    payload: v.object({
      // v2/* (requestPWF)
      get_payment_links: booleanField(), // GET  /v2/pwf/bill
      transaction_report: booleanField(), // POST /v1/report/pwf, GET  /v1/report/pwf, GET  /v1/pwf/payment-history
      settlement_report: booleanField() // GET  /v1/pwf/accept-payment-settlement-report, GET  /v1/report/${reportId}/accept-payment-settlement-report, POST /v1/report/accept-payment-settlement-report
    })
  }),
  // Refund feature gate (Riwayat Refund page and, later, Create Refund).
  //
  // Optional-with-default for the same reason as NEXUS_REFETCH_CONFIG, and it matters more while
  // the flag is still being created in Nexus: a REQUIRED key absent from the response fails
  // safeParse for the WHOLE payload, so fetchNexusVariants returns null and the initial load falls
  // back to DEFAULT_NEXUS_DATA — dragging every already-rolled-out flag back to legacy. Optional
  // keeps the rest of the response intact and leaves this one flag off.
  [KEY.PAY_REFUND_FLAGS]: v.optional(v.object({ payload: v.object({ enabled: booleanField() }) }), {
    payload: { enabled: false }
  }),
  // Optional-with-default for the same reason as PAY_REFUND_FLAGS above — this key does not exist
  // in Nexus yet, and a required-but-absent key would fail safeParse for the whole payload.
  [KEY.PAY_HISTORY_V2]: v.optional(v.object({ payload: v.object({ enabled: booleanField() }) }), {
    payload: { enabled: false }
  }),
  // OnCall hardcode of the BRI top-up account number. `endpointToggleSchema` is
  // optional-with-default, which is what this flag needs while it is still being created in Nexus:
  // a required-but-absent key would fail safeParse for the whole payload and drag every other flag
  // back to its default.
  [KEY.HARDCODE_BRI_ACCOUNT_TOPUP]: endpointToggleSchema
});

export type NexusData = v.InferOutput<typeof nexusSchema>;
